import type { StringKey } from '../strings';

/**
 * The change log's shape and its rules (piece 12, decision 0636): what a person
 * signing in reads on the What's new page, and whether the avatar carries the
 * dot that says there is something there they have not seen.
 *
 * 🔒 The entries are repository content shipped with the build, never a table.
 * They are typed here and held in `entries.ts`, one per landed batch, newest
 * first, and `tools/check-changelog.py` holds the same rules this file's test
 * holds, from the plan's side.
 */

/** Where the What's new page lives. It reads no API: the entries ship in the bundle. */
export const WHATS_NEW_ROUTE = '/whats-new';

/**
 * 🔒 The function-tests areas, in the areas table's own order
 * (`tools/function_test_areas.py`). An entry names the area its change lives
 * in, and the page draws that area as a pill. The check fails where this tuple
 * and the table disagree.
 */
export const CHANGELOG_AREAS = [
  'mobile', 'platform', 'email', 'knowledge', 'assets', 'hr', 'quote', 'sales-order', 'invoice', 'purchase-order', 'work-order', 'item', 'deal', 'company-person',
  'expenses', 'bills-and-payments', 'banking', 'ledger', 'tax', 'files', 'auth', 'settings', 'reports', 'shell',
] as const;
export type ChangeLogArea = (typeof CHANGELOG_AREAS)[number];

/** The pill each area draws on the page, in words a person using the application recognises. */
export const CHANGELOG_AREA_LABELS: Readonly<Record<ChangeLogArea, StringKey>> = {
  // Piece 35, decision 3421: the phone app's area, first in the function-tests table.
  mobile: 'whatsNew.area.mobile',
  platform: 'whatsNew.area.platform',
  // Piece 32: the mail client's area, in the function-tests table's order.
  email: 'whatsNew.area.email',
  // Piece 30: the knowledge base's area, in the function-tests table's order.
  knowledge: 'whatsNew.area.knowledge',
  // Piece 28: the asset register's area, in the function-tests table's order.
  assets: 'whatsNew.area.assets',
  // Piece 34: the HR module's area, in the function-tests table's order.
  hr: 'whatsNew.area.hr',
  quote: 'whatsNew.area.quote',
  'sales-order': 'whatsNew.area.salesOrder',
  invoice: 'whatsNew.area.invoice',
  'purchase-order': 'whatsNew.area.purchaseOrder',
  'work-order': 'whatsNew.area.workOrder',
  item: 'whatsNew.area.item',
  deal: 'whatsNew.area.deal',
  'company-person': 'whatsNew.area.companyPerson',
  expenses: 'whatsNew.area.expenses',
  'bills-and-payments': 'whatsNew.area.billsAndPayments',
  banking: 'whatsNew.area.banking',
  ledger: 'whatsNew.area.ledger',
  tax: 'whatsNew.area.tax',
  files: 'whatsNew.area.files',
  auth: 'whatsNew.area.auth',
  settings: 'whatsNew.area.settings',
  reports: 'whatsNew.area.reports',
  shell: 'whatsNew.area.shell',
};

/**
 * 🔒 One line of an entry (decision 0766): a sentence, or a sentence naming the
 * page it changed where the entry reaches more than one page. A line naming no
 * page stands under the entry's own area.
 */
export type ChangeLogLine = string | { page: string; text: string };

/**
 * One entry: one landed batch, told to the person using the application.
 * `date` is the calendar day it reached main, drawn as that day and never
 * converted through a zone. `lines` are one to eight short sentences (decision 10761), each
 * saying what changed and where to find it; a line may name its own page.
 * `summary` is the one sentence drawn under the title (decision 8700), required
 * from `CHANGELOG_SUMMARY_FROM` on; an entry without one draws its first line.
 */
export interface ChangeLogEntry {
  date: string;
  title: string;
  area: ChangeLogArea;
  lines: readonly ChangeLogLine[];
  summary?: string;
}

/**
 * 🔨 From this day an entry carries a summary, and its summary and every line keep
 * to `CHANGELOG_MAX_WORDS` words (decision 8700). An entry dated earlier is not
 * held, and its lines stay as they were written.
 */
export const CHANGELOG_SUMMARY_FROM = '2026-10-27';
export const CHANGELOG_MAX_WORDS = 20;
/** 🔒 From this day the same two limits are twelve words (decision 10761). */
export const CHANGELOG_TWELVE_FROM = '2026-10-30';
export const CHANGELOG_MAX_WORDS_TWELVE = 12;

/** How many words a sentence carries, counted on its whitespace. */
export function changelogWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

/** A line's sentence, whether or not it names its own page. */
export function changelogLineText(line: ChangeLogLine): string {
  return typeof line === 'string' ? line : line.text;
}

/** One page's changes within a release: the page's name and the sentences under it. */
export interface ChangeLogPage {
  page: string;
  lines: readonly string[];
}

/** One release: the day batches reached main, and the pages they changed. */
export interface ChangeLogRelease {
  date: string;
  pages: readonly ChangeLogPage[];
}

/**
 * 🔒 The page the owner asked for (decision 0766; T244): *"grouping them based on
 * release and then sub-title the page affected, then the changes."* A release is a
 * day batches reached main, newest first. Under it each page the day changed, in
 * the order the day's entries first name it, then that page's lines. A line
 * stands under the page it names, else under its entry's area, read through
 * `areaLabel` so the page's name is the one the area pill always read.
 */
export function changelogReleases(entries: readonly ChangeLogEntry[], areaLabel: (area: ChangeLogArea) => string): ChangeLogRelease[] {
  const releases: { date: string; pages: { page: string; lines: string[] }[] }[] = [];
  for (const entry of entries) {
    let release = releases.find((candidate) => candidate.date === entry.date);
    if (!release) {
      release = { date: entry.date, pages: [] };
      releases.push(release);
    }
    for (const line of entry.lines) {
      // A line routed to another page names it by area key or by its own words; a key reads as that area's label, never as the key.
      const page = typeof line === 'string' ? areaLabel(entry.area) : line.page in CHANGELOG_AREA_LABELS ? areaLabel(line.page as ChangeLogArea) : line.page;
      let group = release.pages.find((candidate) => candidate.page === page);
      if (!group) {
        group = { page, lines: [] };
        release.pages.push(group);
      }
      group.lines.push(changelogLineText(line));
    }
  }
  return releases.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** The apps an entry reaches, drawn as a tag each (decision 8700). */
export type ChangeLogApp = 'web' | 'phone';

/** One line inside an entry's Details: the page it names, where it names one, and its sentence. */
export interface ChangeLogBlockLine {
  page: string | null;
  text: string;
}

/** One entry as the page draws it: its block under its month. */
export interface ChangeLogBlock {
  date: string;
  title: string;
  area: ChangeLogArea;
  areaLabel: string;
  apps: readonly ChangeLogApp[];
  summary: string;
  lines: readonly ChangeLogBlockLine[];
}

/** One month of the log, as `YYYY-MM`, and its entries newest first. */
export interface ChangeLogMonth {
  month: string;
  entries: readonly ChangeLogBlock[];
}

/** The summary drawn under an entry's title: its own, else its first line's sentence (decision 8700). */
export function changelogSummary(entry: ChangeLogEntry): string {
  const own = entry.summary?.trim();
  if (own) return own;
  const first = entry.lines[0];
  return first === undefined ? '' : changelogLineText(first);
}

const PHONE_PAGE = /\bphone\b/i;
/**
 * 🔒 The apps an entry reaches (decision 8700), read from how the entries say it.
 * Phone where the area is the phone app's, or a line names a page whose name holds
 * the word Phone. Web where the area is any other, or a line of a phone entry
 * names a page whose name does not hold Phone. A phone entry that only compares
 * itself to the website reads Phone alone. Web first where both apply.
 */
export function changelogApps(entry: ChangeLogEntry): ChangeLogApp[] {
  const phone = entry.area === 'mobile' || entry.lines.some((line) => typeof line !== 'string' && PHONE_PAGE.test(line.page));
  const web = entry.area !== 'mobile' || entry.lines.some((line) => typeof line !== 'string' && !PHONE_PAGE.test(line.page));
  return [...(web ? (['web'] as const) : []), ...(phone ? (['phone'] as const) : [])];
}

/**
 * 🔒 The page the owner asked for on 2026-10-27 (decision 8700, amending 0766):
 * a month heading, newest first, and under it one block per entry with its title,
 * area label, apps, day, summary and lines. An entry keeps its place in the log
 * within its month, and a line naming an area key reads as that area's label.
 */
export function changelogMonths(entries: readonly ChangeLogEntry[], areaLabel: (area: ChangeLogArea) => string): ChangeLogMonth[] {
  const months: { month: string; entries: ChangeLogBlock[] }[] = [];
  for (const entry of entries) {
    const key = entry.date.slice(0, 7);
    let month = months.find((candidate) => candidate.month === key);
    if (!month) {
      month = { month: key, entries: [] };
      months.push(month);
    }
    month.entries.push({
      date: entry.date,
      title: entry.title,
      area: entry.area,
      areaLabel: areaLabel(entry.area),
      apps: changelogApps(entry),
      summary: changelogSummary(entry),
      lines: entry.lines.map((line) =>
        typeof line === 'string'
          ? { page: null, text: line }
          : { page: line.page in CHANGELOG_AREA_LABELS ? areaLabel(line.page as ChangeLogArea) : line.page, text: line.text }),
    });
  }
  for (const month of months) month.entries.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return months.sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
}

export const CHANGELOG_MIN_LINES = 1;
export const CHANGELOG_MAX_LINES = 8;

/**
 * 🔒 The browser's own memory of the newest entry it has shown (decision 0636).
 * 💰 It is kept per browser, not per person, so a second device shows the dot
 * once more until the page is opened there.
 */
export const WHATS_NEW_SEEN_KEY = 'bms2.whatsNewSeen';

/**
 * What the browser remembers after a visit: the newest entry's day and title.
 * 🔨 The title rides with the day because several batches land on one day, and a
 * day alone would hide an afternoon's entry from somebody who looked that
 * morning. An empty log has nothing to remember and answers the empty string.
 */
export function changelogMarker(entries: readonly ChangeLogEntry[]): string {
  const newest = entries[0];
  return newest === undefined ? '' : `${newest.date} ${newest.title}`;
}

/**
 * Whether the dot is drawn: there is an entry, and the browser's marker is not
 * the newest one's. A browser that has never visited holds no marker, and an
 * unreadable store is read the same way, so both show the dot.
 */
export function changelogUnseen(entries: readonly ChangeLogEntry[], seen: string | null): boolean {
  const marker = changelogMarker(entries);
  return marker !== '' && marker !== seen;
}

/**
 * 🔒 The entries newer than a remembered marker, newest first: what the phone's
 * What's New sheet shows on the first open after an update (piece 35; decision
 * 5361). Nothing where no marker is remembered, since a first sign-in is no
 * update; and where the remembered entry has left the log, every entry dated
 * after its day.
 */
export function changelogSince(entries: readonly ChangeLogEntry[], seen: string | null): ChangeLogEntry[] {
  if (seen === null || seen === '') return [];
  const at = entries.findIndex((entry) => changelogMarker([entry]) === seen);
  if (at >= 0) return entries.slice(0, at);
  const day = seen.slice(0, 10);
  return entries.filter((entry) => entry.date > day);
}

/** What is wrong with the log, one sentence per fault; the empty list is a clean log. */
export function changelogProblems(entries: readonly ChangeLogEntry[]): string[] {
  const problems: string[] = [];
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  entries.forEach((entry, at) => {
    const which = `entry ${at + 1} (${entry.title || 'untitled'})`;
    if (!iso.test(entry.date) || Number.isNaN(Date.parse(`${entry.date}T00:00:00Z`)) || new Date(`${entry.date}T00:00:00Z`).toISOString().slice(0, 10) !== entry.date) {
      problems.push(`${which} carries a date that is not an ISO calendar day: ${entry.date}`);
    }
    if (entry.title.trim() === '') problems.push(`${which} has no title`);
    if (!(CHANGELOG_AREAS as readonly string[]).includes(entry.area)) problems.push(`${which} names an area that is not a function-tests area: ${entry.area}`);
    if (entry.lines.length < CHANGELOG_MIN_LINES || entry.lines.length > CHANGELOG_MAX_LINES) {
      problems.push(`${which} carries ${entry.lines.length} lines, and an entry carries ${CHANGELOG_MIN_LINES} to ${CHANGELOG_MAX_LINES}`);
    }
    if (entry.lines.some((line) => changelogLineText(line).trim() === '')) problems.push(`${which} carries an empty line`);
    if (entry.lines.some((line) => typeof line !== 'string' && line.page.trim() === '')) problems.push(`${which} names an empty page on a line`);
    if (entry.summary !== undefined && entry.summary.trim() === '') problems.push(`${which} carries an empty summary`);
    if (entry.date >= CHANGELOG_SUMMARY_FROM) {
      const limit = entry.date >= CHANGELOG_TWELVE_FROM ? CHANGELOG_MAX_WORDS_TWELVE : CHANGELOG_MAX_WORDS;
      if (entry.summary === undefined) problems.push(`${which} has no summary, and an entry from ${CHANGELOG_SUMMARY_FROM} carries one`);
      else if (changelogWords(entry.summary) > limit) problems.push(`${which} carries a summary over ${limit} words`);
      if (entry.lines.some((line) => changelogWords(changelogLineText(line)) > limit)) problems.push(`${which} carries a line over ${limit} words`);
    }
    const before = entries[at - 1];
    if (before !== undefined && before.date < entry.date) problems.push(`${which} is newer than the entry above it, and the log reads newest first`);
  });
  return problems;
}
